import api from '../../../shared/utils/api';
import type { RadarApproachScope, RadarGenerationContext } from './radarSignalsApi';
import type {
  MarketListRequest,
  MarketListResponse,
  BdrHookRequest,
  BdrHookResponse,
  ColdEmailRequest,
  ColdEmailResponse,
  NewsItem,
  NewsMonitorRequest,
  NewsMonitorResponse,
  FinancialContextRequest,
  FinancialContextResponse,
  WinLossRequest,
  WinLossResponse,
  Visao360Analysis,
} from '../../../shared/types';

export const intelligenceApi = {
  getMarketList: async (payload: MarketListRequest): Promise<MarketListResponse> => {
    const { data } = await api.post<MarketListResponse>('/intelligence/market-list', payload);
    return data;
  },

  getFinancialContext: async (
    payload: FinancialContextRequest,
  ): Promise<FinancialContextResponse> => {
    const { data } = await api.post<FinancialContextResponse>(
      '/intelligence/financial-context',
      payload,
    );
    return data;
  },

  getBdrHook: async (payload: BdrHookRequest & RadarApproachScope): Promise<BdrHookResponse & { radarContext?: RadarGenerationContext }> => {
    const { data } = await api.post<BdrHookResponse & { radarContext?: RadarGenerationContext }>('/sales/bdr-hook', payload);
    return data;
  },

  getColdEmail: async (payload: ColdEmailRequest & RadarApproachScope): Promise<ColdEmailResponse & { radarContext?: RadarGenerationContext }> => {
    const { data } = await api.post<ColdEmailResponse & { radarContext?: RadarGenerationContext }>('/sales/cold-email', payload);
    return data;
  },

  getNewsMonitor: async (payload: NewsMonitorRequest): Promise<NewsMonitorResponse> => {
    const { data } = await api.post<NewsMonitorResponse>('/research/news-monitor', payload);
    return data;
  },

  /**
   * Async, non-blocking variant of the news monitor. Enqueues a research job
   * on the BFF and returns immediately with the enrichmentMessageId — mirrors
   * the contact-unlock enrichment flow so the global "Fila de enriquecimentos"
   * picks it up. Poll completion with getNewsEnrichmentStatus.
   */
  enqueueNewsMonitor: async (payload: {
    companyName: string;
    focus?: string;
    actionUrl?: string;
  }): Promise<{ enrichmentMessageId: string; status: string }> => {
    const { data } = await api.post<{ enrichmentMessageId: string; status: string }>(
      '/research/news-monitor/async',
      payload,
    );
    return data;
  },

  /**
   * Poll the status of an async news-monitor job. `result` is null until the
   * job reaches a terminal state; on `completed` it carries the news payload.
   */
  getNewsEnrichmentStatus: async (
    messageId: string,
  ): Promise<{
    status: string;
    result: { news: NewsItem[]; companyName: string } | null;
  }> => {
    const { data } = await api.get<{
      status: string;
      result: { news: NewsItem[]; companyName: string } | null;
    }>(`/research/enrichment-status/${messageId}`);
    return data;
  },

  analyzeWinLoss: async (payload: WinLossRequest): Promise<WinLossResponse> => {
    const { data } = await api.post<WinLossResponse>('/analysis/winloss', payload);
    return data;
  },

  getCachedVisao360: async (
    cnpj: string,
  ): Promise<{ analysis: Visao360Analysis; stale: boolean; generatedAt: string | null } | null> => {
    // Cache-only lookup: never triggers an LLM generation. The BFF returns
    // ``200 {cached: false}`` em cache miss (Wave 2026-05-21 — antes era
    // 404, mas o browser printava em vermelho no devtools a cada
    // deal-open com Visão 360 default). Cache freshness vem via header
    // ``X-Cache-Status`` (hit | stale | miss) pra render sem 2ª chamada.
    //
    // Compat: o catch ainda trata 404 pra cobrir a janela de deploy
    // onde o FE pode estar rodando contra um BFF antigo (que ainda
    // retorna 404). Quando o BFF estiver totalmente em main, o catch
    // de 404 pode sair em uma limpeza futura.
    try {
      const response = await api.get<Visao360Analysis | { cached: false }>(
        `/intelligence/visao-360/${encodeURIComponent(cnpj)}`,
      );
      const cacheStatus = String(response.headers['x-cache-status'] || '').toLowerCase();
      // BFF novo: miss vem como 200 + header ``X-Cache-Status: miss`` +
      // body ``{cached: false}``. Não dá pra confiar só no body porque
      // o response.data é tipado como union — checa o header primeiro.
      if (cacheStatus === 'miss') return null;
      const generatedAt = (response.headers['x-cache-generated-at'] as string) || null;
      return {
        analysis: response.data as Visao360Analysis,
        stale: cacheStatus === 'stale',
        generatedAt,
      };
    } catch (err) {
      if ((err as { response?: { status?: number } })?.response?.status === 404) return null;
      throw err;
    }
  },

  getVisao360: async (payload: {
    companyName: string;
    corporateContext?: string;
    businessContext?: string;
    newsContext?: string;
    socialContext?: string;
    contactsContext?: string;
    signalsContext?: string;
    signals?: unknown;
    tenantContext?: string;
    cnpj?: string;
    dealId?: string;
    forceRefresh?: boolean;
  }): Promise<{
    analysis: Visao360Analysis;
    cacheStatus: 'hit' | 'stale-revalidating' | 'miss' | null;
    parsingFailed: boolean;
  }> => {
    // Visão 360 fans out to a Gemini generation + financial context enrichment
    // on the backend; it routinely runs past the shared 30s axios default and
    // was being aborted client-side before the response ever arrived (LB log:
    // "client_disconnected_before_any_response" at ~30s). Override to 120s
    // just for this call — the rest of the app keeps the default timeout.
    //
    // The BFF returns 502 + { error: 'llm_parse_failed', analysis } when the
    // upstream LLM produced an unparseable response. Surface that as a soft
    // signal (parsingFailed: true) instead of throwing so the UI can prompt
    // the user to retry without losing whatever partial summary came back.
    try {
      const response = await api.post<Visao360Analysis>(
        '/intelligence/visao-360',
        payload,
        { timeout: 180_000 },
      );
      const cacheStatus = (String(response.headers['x-cache-status'] || '').toLowerCase()
        || null) as 'hit' | 'stale-revalidating' | 'miss' | null;
      return {
        analysis: response.data,
        cacheStatus,
        parsingFailed: Boolean(response.data?.parsingFailed),
      };
    } catch (err) {
      // O BFF devolve 502 + `{ error: 'llm_parse_failed', analysis }`; a lente
      // nomeia exatamente esses dois campos, no molde de
      // `cross-sell/utils/errorMicrocopy.ts`.
      const erro = err as {
        response?: { status?: number; data?: { error?: string; analysis?: Visao360Analysis } };
      };
      if (erro?.response?.status === 502 && erro?.response?.data?.error === 'llm_parse_failed') {
        const fallback = (erro.response?.data?.analysis || {}) as Visao360Analysis;
        return { analysis: fallback, cacheStatus: null, parsingFailed: true };
      }
      throw err;
    }
  },

  searchLinkedInDecisionMakers: async (payload: {
    companyName: string;
    dealId?: string;
    roles?: string[];
    companyLinkedinUrl?: string;
    cnpj?: string;
    sector?: string;
  }): Promise<LinkedInSearchResponse> => {
    const { data } = await api.post<LinkedInSearchResponse>('/intelligence/linkedin-search', payload);
    return data;
  },

  searchLinkedInCompany: async (payload: {
    companyName: string;
    dealId?: string;
    razaoSocial?: string;
    nomeFantasia?: string;
    cnpj?: string;
  }): Promise<{ found: boolean; linkedinUrl: string | null }> => {
    const { data } = await api.post<{ found: boolean; linkedinUrl: string | null }>(
      '/intelligence/linkedin-search-company',
      payload,
    );
    return data;
  },

  fetchLinkedInPosts: async (payload: {
    cnpj: string;
    companyName: string;
    linkedinUrl?: string;
    forceRefresh?: boolean;
  }): Promise<LinkedInPostsResponse> => {
    const { data } = await api.post<LinkedInPostsResponse>(
      '/intelligence/linkedin-company-posts',
      payload,
    );
    return data;
  },
};

export interface LinkedInProfile {
  name: string;
  title: string;
  linkedinUrl: string;
  companyName?: string;
  confidence: number;
  source: string;
}

export interface LinkedInSearchResponse {
  companyName: string;
  linkedinUrl?: string;
  profiles: LinkedInProfile[];
  totalFound: number;
  strategiesUsed: string[];
}

export interface LinkedInPost {
  title: string;
  snippet: string;
  url: string;
  publishedAt?: string;
  source: string;
}

export interface LinkedInPostsResponse {
  cnpj?: string;
  companyName?: string;
  linkedinUrl?: string;
  posts: LinkedInPost[];
  totalFound?: number;
  fetchedAt?: string;
  stale?: boolean;
  strategiesUsed?: string[];
}
